import { useState } from 'react';
import colors from '../../../../../assets/colors/Colors.json';
import { useClassesTable } from '../../../../../components/Classes/hooks/useClassesTable';
import { ClassesModal } from '../../../../../components/Classes/Modals/ClassesModal';
import { InheritClassesModal } from '../../../../../components/Classes/Modals/InheritClassesModal';
import { TimetableClassesTable } from '../../../../../components/Classes/Tables/TimetableClassesTable';
import { useDeleteClasses } from '../../../../../hooks/useDeleteClasses';
import { useLoadBasicData } from '../../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../../hooks/usePermissionChecker';
import { use_UpdateClassesColorsMutation } from '../../../../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserDbErrorToast, showUserErrorToast } from '../../../../../utils/toast';

export const TimetableClassesPage = () => {
  const permissions = usePermissionChecker();
  const context = useMemorizedCacheTag('CLASSES_FIXED');

  const { deleteClass } = useDeleteClasses();
  const [, updateColor] = use_UpdateClassesColorsMutation();

  const { data, DeleteDialog, confirmDelete } = useClassesTable();

  const [classesModalOpen, setClassesModalOpen] = useState<boolean>(false);
  const [inheritModalOpen, setInheritModalOpen] = useState<boolean>(false);
  const [classUuid, setClassUuid] = useState<null | string>(null);
  const [loading, setLoading] = useState<boolean>(false);

  function handleAdd() {
    setClassUuid(null);
    setClassesModalOpen(true);
  }

  function handleEdit(uuid: string) {
    setClassUuid(uuid);
    setClassesModalOpen(true);
  }

  async function handleAddColors(uuids: string[]) {
    uuids.forEach((uuid) => {
      const color = colors[Math.floor(Math.random() * colors.length)];
      updateColor({ color: color.html, uuid: uuid }, context);
    });
  }

  async function handleDelete(uuids: string[]) {
    const confirmResult = await confirmDelete();
    if (!confirmResult) return;

    const classesToDelete = data.filter((c) => uuids.includes(c.uuid));

    if (!classesToDelete || classesToDelete.length === 0) {
      showUserErrorToast({ error: '' });
      return;
    }

    let deleteCount = 0;

    setLoading(true);

    for (const cl of classesToDelete) {
      const { error } = await deleteClass(cl.uuid);
      if (error) {
        showUserDbErrorToast();
        return;
      } else {
        deleteCount++;
      }
    }

    if (deleteCount === 1) {
      showSuccessDeleteToast([classesToDelete[0].name]);
    } else {
      showSuccessDeleteToast();
    }

    setLoading(false);
  }

  return (
    <>
      <TimetableClassesTable
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onInherit={() => setInheritModalOpen(true)}
        onAddColor={handleAddColors}
        onDelete={handleDelete}
        readonly={!permissions?.canWriteTimetable()}
      />

      <ClassesModal
        classUuid={classUuid}
        isOpen={classesModalOpen}
        onClose={() => {
          setClassUuid(null);
          setClassesModalOpen(false);
        }}
      />

      <InheritClassesModal isOpen={inheritModalOpen} onClose={() => setInheritModalOpen(false)} />

      <DeleteDialog />
    </>
  );
};
